import _ from 'lodash';
import AnimateHeight from 'react-animate-height';
import PropTypes from 'prop-types';
import React from 'react';
import scrollparent from 'scrollparent';

import ExpandedOrderTransactionBase from './expanded_order_transaction_base';
import { FlightAttributesPropTypes } from './expanded_flight_transaction';
import { FlightTransactionDef, OrderTransactionDef, StayTransactionDef } from 'models/configuration/transaction_def';
import { OrderAttributesPropTypes } from './order_transactions';
import { ProfileCardType } from 'models/configuration/profile_card_def';
import { StayAttributesPropTypes } from './stay_transactions';
import { transactionDefTypeReflect } from 'models/configuration/transaction_def_from_js';
import VisibleCustomAttribute from '../custom_attributes/visible_custom_attribute';

const TRANSACTION_ANIMATION_DURATION = 300;
const SCROLLING_PADDING = 10;

export { TRANSACTION_ANIMATION_DURATION };

export class ExpandedTransactionBase extends React.Component {
  render() {
    return (
      <div className="transaction-expandedInformation" ref={node => (this.expandedTransaction = node)}>
        <div className="transaction-expandedInformation-topTriangle" ref={node => (this.triangle = node)}>
          <div className="transaction-expandedInformation-topTriangle-border" />
        </div>
        {this.props.headerRenderer(this.props.attributes)}
        <hr className="transaction-middleBorder" />
        {this.renderAttributes()}
      </div>
    );
  }

  renderAttributes() {
    return (
      <div className="transaction-expandedInformation-optionalAttributes">
        {_.map(this.props.transactionDef.attributes, def => {
          const key = `optionalAttributes-${def.attr}`;
          return (
            <VisibleCustomAttribute
              className="transactionAttribute"
              def={def}
              key={key}
              value={this.props.attributes[def.attr]}
              location={{
                type: ProfileCardType.TRANSACTIONS,
                subType: transactionDefTypeReflect.instanceToType(this.props.transactionDef),
              }}
            />
          );
        })}
      </div>
    );
  }
}

ExpandedTransactionBase.propTypes = {
  attributes: PropTypes.oneOfType([FlightAttributesPropTypes, OrderAttributesPropTypes, StayAttributesPropTypes]),
  headerRenderer: PropTypes.func.isRequired,
  transactionDef: PropTypes.oneOfType([
    PropTypes.instanceOf(FlightTransactionDef),
    PropTypes.instanceOf(OrderTransactionDef),
    PropTypes.instanceOf(StayTransactionDef),
  ]).isRequired,
};

export default class ExpandedTransaction extends React.Component {
  constructor(props) {
    super(props);

    this.state = { isExpanded: props.isExpanded, transactionHeight: props.isExpanded ? 'auto' : 0 };
    this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
    this.handleAnimationStart = this.handleAnimationStart.bind(this);
    this.setRef = this.setRef.bind(this);
    this.renderTransactionBase = this.renderTransactionBase.bind(this);
  }

  componentWillUnmount() {
    clearTimeout(this.closeTimeout);
    this.request && cancelAnimationFrame(this.request);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isExpanded && !this.props.isExpanded) {
      clearTimeout(this.closeTimeout);
      this.setState({ isExpanded: true, transactionHeight: 'auto' });
    } else if (!nextProps.isExpanded && this.props.isExpanded) {
      // We delay the unmounting of the flight transaction when closing until after the closing animation has completed
      this.closeTimeout = setTimeout(() => this.setState({ isExpanded: false }), TRANSACTION_ANIMATION_DURATION);
      this.setState({ transactionHeight: 0 });
    }
  }

  render() {
    return (
      <div className="transaction-expandedInformation-wrapper" ref={this.setRef}>
        <AnimateHeight
          animateOpacity
          className="transaction-detailed"
          duration={TRANSACTION_ANIMATION_DURATION}
          height={this.state.transactionHeight}
          onAnimationEnd={this.handleAnimationEnd}
          onAnimationStart={this.handleAnimationStart}
        >
          <span style={{ display: 'flex', flexDirection: 'column' }}>{this.renderTransactionBase()}</span>
        </AnimateHeight>
      </div>
    );
  }

  renderTransactionBase() {
    if (!this.state.isExpanded) {
      return null;
    } else if (this.props.transactionDef instanceof OrderTransactionDef) {
      return (
        <ExpandedOrderTransactionBase
          attributes={this.props.attributes}
          transactionDef={this.props.transactionDef}
          headerRenderer={this.props.headerRenderer}
        />
      );
    } else {
      return (
        <ExpandedTransactionBase
          attributes={this.props.attributes}
          transactionDef={this.props.transactionDef}
          headerRenderer={this.props.headerRenderer}
        />
      );
    }
  }

  handleAnimationStart() {
    const increment = 20;
    const duration = TRANSACTION_ANIMATION_DURATION + 40; // Set duration past to help with animation completing

    let currentTime = 0;
    const animateScroll = () => {
      const parent = scrollparent(this.wrapper);
      currentTime += increment;

      if (parent) {
        let scrollparentBottom = parent.getBoundingClientRect().bottom;
        let wrapperBottom = this.wrapper.getBoundingClientRect().bottom;
        const diff = wrapperBottom + SCROLLING_PADDING - scrollparentBottom;

        if (diff > 0) {
          parent.scrollTop = parent.scrollTop + diff;
        }

        if (currentTime < duration) {
          this.request = requestAnimationFrame(animateScroll);
        }
      }
    };
    animateScroll();
  }

  handleAnimationEnd() {
    // Cleanup leftover space if request frame didn't fire enough before animation ended
    const parent = scrollparent(this.wrapper);
    let scrollparentBottom = parent.getBoundingClientRect().bottom;
    let wrapperBottom = this.wrapper.getBoundingClientRect().bottom;
    const diff = wrapperBottom + SCROLLING_PADDING - scrollparentBottom;
    if (diff > 0) {
      parent.scrollTop = parent.scrollTop + diff;
    }

    this.request && cancelAnimationFrame(this.request);
    this.request = null;
  }

  setRef(node) {
    this.wrapper = node;
  }
}

ExpandedTransaction.propTypes = {
  attributes: PropTypes.oneOfType([FlightAttributesPropTypes, OrderAttributesPropTypes, StayAttributesPropTypes]),
  headerRenderer: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool,
  transactionDef: PropTypes.oneOfType([
    PropTypes.instanceOf(FlightTransactionDef),
    PropTypes.instanceOf(OrderTransactionDef),
    PropTypes.instanceOf(StayTransactionDef),
  ]).isRequired,
};



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/summary/transactions/expanded_transaction.jsx